import { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { IndianRupee, Package, ShoppingCart, Users, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const STATUS_COLORS = {
  paid: "text-brand", pending: "text-yellow-400", expired: "text-zinc-500", failed: "text-red-400",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const cards = [
    { label: "Total Revenue", value: inr(stats.revenue), icon: IndianRupee, testid: "stat-revenue" },
    { label: "Active Orders", value: stats.active_orders, icon: ShoppingCart, testid: "stat-active-orders" },
    { label: "Products", value: stats.total_products, icon: Package, testid: "stat-products" },
    { label: "Customers", value: stats.total_customers, icon: Users, testid: "stat-customers" },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="border border-zinc-800 bg-zinc-950 p-5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{c.label}</span>
              <c.icon className="h-4 w-4 text-brand" />
            </div>
            <p className="font-display text-2xl lg:text-3xl font-extrabold mt-3">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="border border-zinc-800 bg-zinc-950 p-5">
        <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Revenue · Last 7 days</h2>
        {stats.revenue_chart.length === 0 ? (
          <p className="text-center py-12 font-mono text-xs text-zinc-600 uppercase tracking-widest">No paid orders yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats.revenue_chart}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ccff00" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#ccff00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", fontSize: 12 }} formatter={(v) => inr(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#ccff00" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="border border-zinc-800 bg-zinc-950 p-5">
        <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Recent Orders</h2>
        <div className="space-y-2">
          {stats.recent_orders.length === 0 && <p className="font-mono text-xs text-zinc-600 uppercase tracking-widest py-4">No orders yet</p>}
          {stats.recent_orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between py-2 border-b border-zinc-900 last:border-0">
              <div>
                <p className="font-mono text-sm">#{o.order_no}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{o.user_email}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm">{inr(o.total)}</p>
                <p className={`font-mono text-[10px] uppercase tracking-widest ${STATUS_COLORS[o.payment_status] || "text-zinc-500"}`}>{o.payment_status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
